import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../bizsuite-app';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import FormField from '../components/FormField';
import CostSheet from '../components/CostSheet';
import BillingPlanEditor from '../components/BillingPlanEditor';
import PricingComputation from '../components/PricingComputation';
import { useFormDirty } from '../hooks/useFormDirty';
import { useRoleOptions } from '../hooks/useRoleOptions';
import { formatCurrency as fmtCur } from '../utils/currency';
import { formatDate } from '../utils/date';

const STATUS_OPTIONS = ['draft', 'in_review', 'approved', 'rejected'];
const STATUS_BADGES = {
  draft: 'badge-gray', in_review: 'badge-yellow', approved: 'badge-green', rejected: 'badge-red', active: 'badge-green', inactive: 'badge-gray'
};

const EMPTY_RATE_CARD = { name: '', geography: '', customer_tier: '', effective_from: '', effective_to: '', status: 'active' };
const EMPTY_ESTIMATE = { name: '', opportunity_id: '', methodology: '', status: 'draft', pricing_schema_id: '' };
const EMPTY_LINE = { item_number: '', phase: '', role: '', effort_days: '', rate: '', extended_cost: '', extended_billing: '', notes: '' };
const EMPTY_COST = { category: '', description: '', amount: '', currency: 'INR' };
const EMPTY_RC_LINE = { role: '', billing_rate: '', internal_cost_rate: '', currency: 'INR' };

/** Decide which section this page shows based on the route. The sidebar
 *  has two separate entries (Estimates, Rate Cards) both pointing at the
 *  same component; the route disambiguates.  */
function modeFromPath() {
  if (typeof window === 'undefined') return 'estimates';
  return window.location.pathname.includes('/rate-cards') ? 'ratecards' : 'estimates';
}

export default function Estimates() {
  // Tab state is initialized from the route so the Rate Cards menu shows only
  // Rate Cards and the Estimates menu shows only Effort Estimates — no manual
  // tab switcher anymore.
  const [tab] = useState(modeFromPath());
  const [estimates, setEstimates] = useState([]);
  const [rateCards, setRateCards] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [pricingSchemas, setPricingSchemas] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Estimate modals
  const [estModalOpen, setEstModalOpen] = useState(false);
  const [editingEst, setEditingEst] = useState(null);
  const [estForm, setEstForm] = useState({ ...EMPTY_ESTIMATE });
  const [estErrors, setEstErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Copy-to-opportunity modal
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copySource, setCopySource] = useState(null); // { estimateId, versionId? }
  const [copyTargetOpp, setCopyTargetOpp] = useState('');
  const [copyStartDate, setCopyStartDate] = useState('');
  const [copyVersions, setCopyVersions] = useState([]);
  const [copySelectedVersion, setCopySelectedVersion] = useState('');

  // Estimate detail
  const [selectedEst, setSelectedEst] = useState(null);
  const [detailTab, setDetailTab] = useState('effort'); // 'effort' | 'costsheet'
  const [costSheetLine, setCostSheetLine] = useState('consolidated');
  const [estLines, setEstLines] = useState([]);
  const [estCosts, setEstCosts] = useState([]);
  const [estSummary, setEstSummary] = useState(null);
  const [lineModalOpen, setLineModalOpen] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [lineForm, setLineForm] = useState({ ...EMPTY_LINE });
  const [costModalOpen, setCostModalOpen] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [costForm, setCostForm] = useState({ ...EMPTY_COST });

  // Rate card modals
  const [rcModalOpen, setRcModalOpen] = useState(false);
  const [editingRc, setEditingRc] = useState(null);
  const [rcForm, setRcForm] = useState({ ...EMPTY_RATE_CARD });
  const [rcErrors, setRcErrors] = useState({});
  const [selectedRc, setSelectedRc] = useState(null);
  const [rcLines, setRcLines] = useState([]);
  const [rcLineModalOpen, setRcLineModalOpen] = useState(false);
  const [editingRcLine, setEditingRcLine] = useState(null);
  const [rcLineForm, setRcLineForm] = useState({ ...EMPTY_RC_LINE });

  const { dirty: estDirty, markDirty: markEstDirty, resetDirty: resetEstDirty } = useFormDirty();
  const { dirty: rcDirty, markDirty: markRcDirty, resetDirty: resetRcDirty } = useFormDirty();
  const { dirty: detailDirty, markDirty: markDetailDirty, resetDirty: resetDetailDirty } = useFormDirty();
  // Canonical consulting-role list from metadata_lookups (category='role'),
  // used for both Effort Line and Rate Card Line forms.
  const { roles: ROLE_OPTIONS } = useRoleOptions();

  const fetchEstimates = useCallback(async () => {
    try {
      const [estRes, oppRes, psRes, custRes] = await Promise.all([
        apiFetch('/api/estimates/effort-estimates'),
        apiFetch('/api/crm/opportunities?limit=200'),
        apiFetch('/api/pricing-schemas'),
        apiFetch('/api/customers?limit=500'),
      ]);
      if (estRes.ok)  { const d = await estRes.json();  setEstimates(d.estimates || []); }
      if (oppRes.ok)  { const d = await oppRes.json();  setOpportunities(d.opportunities || []); }
      if (psRes.ok)   { const d = await psRes.json();   setPricingSchemas(d.schemas || []); }
      if (custRes.ok) { const d = await custRes.json(); setCustomers(d.customers || []); }
    } catch (e) { /* handled */ }
    setLoading(false);
  }, []);

  const fetchRateCards = useCallback(async () => {
    try {
      const res = await apiFetch('/api/estimates/rate-cards');
      if (res.ok) { const d = await res.json(); setRateCards(d.rateCards || []); }
    } catch (e) { /* handled */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchEstimates();
    fetchRateCards();
  }, [fetchEstimates, fetchRateCards]);

  // ---- Estimate CRUD ----
  function openCreateEst() { setEditingEst(null); setEstForm({ ...EMPTY_ESTIMATE }); setEstErrors({}); resetEstDirty(); setEstModalOpen(true); }
  function openEditEst(est) {
    setEditingEst(est);
    setEstForm({ name: est.name || '', opportunity_id: est.opportunity_id || '', methodology: est.methodology || '', status: est.status || 'draft', pricing_schema_id: est.pricing_schema_id || '' });
    setEstErrors({}); resetEstDirty(); setEstModalOpen(true);
  }

  async function saveEstimate() {
    if (!estForm.name.trim()) { setEstErrors({ name: 'Name is required' }); return; }
    if (!estForm.opportunity_id) { setEstErrors({ opportunity_id: 'Opportunity is required' }); return; }
    setSaving(true);
    const body = { name: estForm.name, opportunityId: estForm.opportunity_id, methodology: estForm.methodology, status: estForm.status, pricingSchemaId: estForm.pricing_schema_id || null };
    const url = editingEst ? `/api/estimates/effort-estimates/${editingEst.id}` : '/api/estimates/effort-estimates';
    const method = editingEst ? 'PUT' : 'POST';
    try {
      const res = await apiFetch(url, { method, body: JSON.stringify(body) });
      if (res.ok) { resetEstDirty(); setEstModalOpen(false); fetchEstimates(); }
      else { const d = await res.json(); setEstErrors({ _general: d.error || 'Failed to save' }); }
    } catch (e) { setEstErrors({ _general: 'Network error' }); }
    setSaving(false);
  }

  async function deleteEstimate(est) {
    if (!window.confirm(`Delete estimate "${est.name}"?`)) return;
    try { await apiFetch(`/api/estimates/effort-estimates/${est.id}`, { method: 'DELETE' }); fetchEstimates(); } catch (e) { /* ignore */ }
  }

  async function duplicateEstimate(est) {
    try {
      const res = await apiFetch(`/api/estimates/effort-estimates/${est.id}/duplicate`, { method: 'POST' });
      if (res.ok) fetchEstimates();
    } catch (e) { /* ignore */ }
  }

  async function openCopyToOpp(est) {
    setCopySource({ estimateId: est.id });
    setCopyTargetOpp('');
    setCopyStartDate('');
    setCopySelectedVersion('');
    // Load versions for this estimate
    try {
      const res = await apiFetch(`/api/estimates/effort-estimates/${est.id}/cost-sheet-versions`);
      if (res.ok) { const d = await res.json(); setCopyVersions(d.versions || []); }
      else { setCopyVersions([]); }
    } catch (e) { setCopyVersions([]); }
    setCopyModalOpen(true);
  }

  async function executeCopy() {
    if (!copyTargetOpp) return;
    setSaving(true);
    try {
      const endpoint = copySelectedVersion
        ? `/api/estimates/effort-estimates/${copySource.estimateId}/copy-version-to-opportunity`
        : `/api/estimates/effort-estimates/${copySource.estimateId}/copy-to-opportunity`;
      const body = {
        targetOpportunityId: copyTargetOpp,
        startDate: copyStartDate || undefined,
        versionId: copySelectedVersion || undefined
      };
      const res = await apiFetch(endpoint, { method: 'POST', body: JSON.stringify(body) });
      if (res.ok) {
        const d = await res.json();
        setCopyModalOpen(false);
        fetchEstimates();
        window.alert(`Estimate copied successfully.${d.ratesRefreshed ? ' Rates refreshed from rate card.' : ' No matching rate card found — rates unchanged.'}`);
      } else {
        const d = await res.json();
        window.alert(d.error || 'Failed to copy estimate');
      }
    } catch (e) { window.alert('Network error'); }
    setSaving(false);
  }

  // ---- Estimate detail (lines, costs, summary) ----
  async function openEstimateDetail(est) {
    setSelectedEst(est);
    try {
      const [linesRes, costsRes, summaryRes] = await Promise.all([
        apiFetch(`/api/estimates/effort-estimates/${est.id}/lines`),
        apiFetch(`/api/estimates/effort-estimates/${est.id}/costs`),
        apiFetch(`/api/estimates/effort-estimates/${est.id}/summary`)
      ]);
      if (linesRes.ok) { const d = await linesRes.json(); setEstLines(d.lines || []); }
      if (costsRes.ok) { const d = await costsRes.json(); setEstCosts(d.costs || []); }
      if (summaryRes.ok) { const d = await summaryRes.json(); setEstSummary(d.summary || null); }
    } catch (e) { /* ignore */ }
  }

  function closeEstimateDetail() { setSelectedEst(null); setEstLines([]); setEstCosts([]); setEstSummary(null); }

  // Quick-jump: open an estimate straight into its Line Items or Cost Sheet tab
  async function openLineItems(est)  { setDetailTab('effort');    await openEstimateDetail(est); }
  async function openCostSheet(est)  { setDetailTab('costsheet'); await openEstimateDetail(est); }

  async function refreshDetail() { if (selectedEst) await openEstimateDetail(selectedEst); }

  // ---- Effort Lines CRUD ----
  function openCreateLine() {
    // Default the new line's item number to (max of existing + 10) so it lands
    // at the bottom of the list but still leaves gaps for manual re-ordering.
    const nextItem = (estLines.reduce((m, l) => Math.max(m, Number(l.item_number) || 0), 0)) + 10;
    setEditingLine(null);
    setLineForm({ ...EMPTY_LINE, item_number: String(nextItem) });
    resetDetailDirty();
    setLineModalOpen(true);
  }
  function openEditLine(line) {
    setEditingLine(line);
    setLineForm({
      item_number: line.item_number != null ? String(line.item_number) : '',
      phase: line.phase || '', role: line.role || '',
      effort_days: line.effort_days || '', rate: line.rate || '',
      extended_cost: line.extended_cost || '', extended_billing: line.extended_billing || '',
      notes: line.notes || '',
    });
    resetDetailDirty(); setLineModalOpen(true);
  }

  async function saveLine() {
    setSaving(true);
    const body = {
      itemNumber: lineForm.item_number !== '' ? parseInt(lineForm.item_number, 10) || 0 : undefined,
      phase: lineForm.phase, role: lineForm.role,
      effortDays: lineForm.effort_days, rate: lineForm.rate,
      extendedCost: lineForm.extended_cost, extendedBilling: lineForm.extended_billing,
      notes: lineForm.notes,
    };
    const url = editingLine ? `/api/estimates/effort-estimates/${selectedEst.id}/lines/${editingLine.id}` : `/api/estimates/effort-estimates/${selectedEst.id}/lines`;
    const method = editingLine ? 'PUT' : 'POST';
    try { const res = await apiFetch(url, { method, body: JSON.stringify(body) }); if (res.ok) { resetDetailDirty(); setLineModalOpen(false); refreshDetail(); fetchEstimates(); } } catch (e) { /* ignore */ }
    setSaving(false);
  }

  async function deleteLine(line) {
    if (!window.confirm('Delete this line item?')) return;
    try { await apiFetch(`/api/estimates/effort-estimates/${selectedEst.id}/lines/${line.id}`, { method: 'DELETE' }); refreshDetail(); fetchEstimates(); } catch (e) { /* ignore */ }
  }

  // ---- Cost CRUD ----
  function openCreateCost() { setEditingCost(null); setCostForm({ ...EMPTY_COST }); resetDetailDirty(); setCostModalOpen(true); }
  function openEditCost(cost) {
    setEditingCost(cost);
    setCostForm({ category: cost.category || '', description: cost.description || '', amount: cost.amount || '', currency: cost.currency || 'INR' });
    resetDetailDirty(); setCostModalOpen(true);
  }

  async function saveCost() {
    setSaving(true);
    const body = { category: costForm.category, description: costForm.description, amount: costForm.amount, currency: costForm.currency };
    const url = editingCost ? `/api/estimates/effort-estimates/${selectedEst.id}/costs/${editingCost.id}` : `/api/estimates/effort-estimates/${selectedEst.id}/costs`;
    const method = editingCost ? 'PUT' : 'POST';
    try { const res = await apiFetch(url, { method, body: JSON.stringify(body) }); if (res.ok) { resetDetailDirty(); setCostModalOpen(false); refreshDetail(); fetchEstimates(); } } catch (e) { /* ignore */ }
    setSaving(false);
  }

  async function deleteCost(cost) {
    if (!window.confirm('Delete this cost item?')) return;
    try { await apiFetch(`/api/estimates/effort-estimates/${selectedEst.id}/costs/${cost.id}`, { method: 'DELETE' }); refreshDetail(); fetchEstimates(); } catch (e) { /* ignore */ }
  }

  // ---- Rate Card CRUD ----
  function openCreateRc() { setEditingRc(null); setRcForm({ ...EMPTY_RATE_CARD }); setRcErrors({}); resetRcDirty(); setRcModalOpen(true); }
  function openEditRc(rc) {
    setEditingRc(rc);
    setRcForm({ name: rc.name || '', geography: rc.geography || '', customer_tier: rc.customer_tier || '', effective_from: rc.effective_from ? rc.effective_from.substring(0, 10) : '', effective_to: rc.effective_to ? rc.effective_to.substring(0, 10) : '', status: rc.status || 'active' });
    setRcErrors({}); resetRcDirty(); setRcModalOpen(true);
  }

  async function saveRateCard() {
    if (!rcForm.name.trim()) { setRcErrors({ name: 'Name is required' }); return; }
    setSaving(true);
    const body = { name: rcForm.name, geography: rcForm.geography, customerTier: rcForm.customer_tier, effectiveFrom: rcForm.effective_from || null, effectiveTo: rcForm.effective_to || null, status: rcForm.status };
    const url = editingRc ? `/api/estimates/rate-cards/${editingRc.id}` : '/api/estimates/rate-cards';
    const method = editingRc ? 'PUT' : 'POST';
    try { const res = await apiFetch(url, { method, body: JSON.stringify(body) }); if (res.ok) { resetRcDirty(); setRcModalOpen(false); fetchRateCards(); } } catch (e) { /* ignore */ }
    setSaving(false);
  }

  async function deleteRateCard(rc) {
    if (!window.confirm(`Delete rate card "${rc.name}"?`)) return;
    try { await apiFetch(`/api/estimates/rate-cards/${rc.id}`, { method: 'DELETE' }); fetchRateCards(); } catch (e) { /* ignore */ }
  }

  // ---- Rate Card Lines ----
  async function openRcDetail(rc) {
    setSelectedRc(rc);
    try { const res = await apiFetch(`/api/estimates/rate-cards/${rc.id}/lines`); if (res.ok) { const d = await res.json(); setRcLines(d.lines || []); } } catch (e) { /* ignore */ }
  }

  function closeRcDetail() { setSelectedRc(null); setRcLines([]); }

  function openCreateRcLine() { setEditingRcLine(null); setRcLineForm({ ...EMPTY_RC_LINE }); resetDetailDirty(); setRcLineModalOpen(true); }
  function openEditRcLine(line) {
    setEditingRcLine(line);
    setRcLineForm({ role: line.role || '', billing_rate: line.billing_rate || '', internal_cost_rate: line.internal_cost_rate || '', currency: line.currency || 'INR' });
    resetDetailDirty(); setRcLineModalOpen(true);
  }

  async function saveRcLine() {
    setSaving(true);
    const body = { role: rcLineForm.role, billingRate: rcLineForm.billing_rate, internalCostRate: rcLineForm.internal_cost_rate, currency: rcLineForm.currency };
    const url = editingRcLine ? `/api/estimates/rate-cards/${selectedRc.id}/lines/${editingRcLine.id}` : `/api/estimates/rate-cards/${selectedRc.id}/lines`;
    const method = editingRcLine ? 'PUT' : 'POST';
    try {
      const res = await apiFetch(url, { method, body: JSON.stringify(body) });
      if (res.ok) { resetDetailDirty(); setRcLineModalOpen(false); openRcDetail(selectedRc); }
    } catch (e) { /* ignore */ }
    setSaving(false);
  }

  async function deleteRcLine(line) {
    if (!window.confirm('Delete this rate card line?')) return;
    try { await apiFetch(`/api/estimates/rate-cards/${selectedRc.id}/lines/${line.id}`, { method: 'DELETE' }); openRcDetail(selectedRc); } catch (e) { /* ignore */ }
  }

  const oppOptions = opportunities.map(o => ({ value: o.id, label: o.name }));

  const fmtCurrency = (v) => fmtCur(v);

  // ---- Estimate detail view ----
  if (selectedEst) {
    return (
      <div>
        {/* Header + detail tabs — all on one line */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={closeEstimateDetail}>{'\u2190'} Back</button>
          <h3 style={{ margin: 0, fontSize: 16 }}>{selectedEst.name}</h3>
          <span style={{ color: '#64748B', fontSize: 12 }}>v{selectedEst.version || 1}</span>
          <span className={`badge ${STATUS_BADGES[selectedEst.status] || 'badge-gray'}`}>{selectedEst.status}</span>
          {/* Description icon — hover for the full text. Only shown when the
              estimate actually has a description. */}
          {selectedEst.description && (
            <span
              title={selectedEst.description}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 24, height: 24, borderRadius: 12,
                background: 'rgba(96,165,250,0.12)',
                border: '1px solid rgba(96,165,250,0.3)',
                color: '#60A5FA', cursor: 'help',
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            </span>
          )}

          <div style={{ flex: 1 }} />

          {/* Tabs — right-aligned on the same line */}
          <div style={{ display: 'flex', gap: 6 }}>
            <button className={`btn ${detailTab === 'effort' ? 'btn-primary' : 'btn-secondary'}`} style={{ padding: '6px 14px', fontSize: 13 }} onClick={() => setDetailTab('effort')}>Effort Lines</button>
            <button className={`btn ${detailTab === 'costsheet' ? 'btn-primary' : 'btn-secondary'}`} style={{ padding: '6px 14px', fontSize: 13 }} onClick={() => setDetailTab('costsheet')}>Cost Sheet</button>
            <button className={`btn ${detailTab === 'billingplan' ? 'btn-primary' : 'btn-secondary'}`} style={{ padding: '6px 14px', fontSize: 13 }} onClick={() => setDetailTab('billingplan')}>Billing Plan</button>
          </div>
        </div>

        {detailTab === 'effort' && (
          <>
            {estSummary && (
              <div className="card" style={{ marginBottom: 20, padding: 20 }}>
                <h4 style={{ marginTop: 0 }}>Margin Calculator</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16 }}>
                  <div><div style={{ color: '#64748B', fontSize: 12 }}>Total Effort Days</div><div style={{ fontSize: 20, fontWeight: 600 }}>{estSummary.totalEffortDays}</div></div>
                  <div><div style={{ color: '#64748B', fontSize: 12 }}>Total Cost</div><div style={{ fontSize: 20, fontWeight: 600 }}>{fmtCurrency(estSummary.totalCost)}</div></div>
                  <div><div style={{ color: '#64748B', fontSize: 12 }}>Total Billing</div><div style={{ fontSize: 20, fontWeight: 600 }}>{fmtCurrency(estSummary.totalBilling)}</div></div>
                  <div><div style={{ color: '#64748B', fontSize: 12 }}>Profit</div><div style={{ fontSize: 20, fontWeight: 600, color: estSummary.profit >= 0 ? '#10B981' : '#EF4444' }}>{fmtCurrency(estSummary.profit)}</div></div>
                  <div><div style={{ color: '#64748B', fontSize: 12 }}>Margin %</div><div style={{ fontSize: 20, fontWeight: 600, color: estSummary.marginPct >= 20 ? '#10B981' : '#F59E0B' }}>{estSummary.marginPct}%</div></div>
                  <div><div style={{ color: '#64748B', fontSize: 12 }}>Additional Costs</div><div style={{ fontSize: 20, fontWeight: 600 }}>{fmtCurrency(estSummary.additionalCosts)}</div></div>
                </div>
              </div>
            )}

            <div className="card" style={{ marginBottom: 20, padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h4 style={{ margin: 0 }}>Effort Lines</h4>
                <button className="btn btn-primary" onClick={openCreateLine}>+ Add Line</button>
              </div>
              <DataTable
                columns={[
                  { key: 'item_number', label: 'Item #', sortable: true, type: 'number', align: 'right', width: '70px' },
                  { key: 'phase', label: 'Phase', sortable: true },
                  { key: 'role', label: 'Role', sortable: true },
                  { key: 'effort_days', label: 'Days', sortable: true, type: 'number' },
                  { key: 'rate', label: 'Rate', sortable: true, render: fmtCurrency, align: 'right' },
                  { key: 'extended_cost', label: 'Cost', sortable: true, render: fmtCurrency, align: 'right' },
                  { key: 'extended_billing', label: 'Billing', sortable: true, render: fmtCurrency, align: 'right' },
                  { key: 'notes', label: 'Notes' }
                ]}
                data={estLines}
                actions={[
                  { label: 'Edit', onClick: openEditLine },
                  { label: 'Delete', onClick: deleteLine }
                ]}
                emptyMessage="No effort lines yet."
              />
            </div>

            <div className="card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h4 style={{ margin: 0 }}>Additional Costs</h4>
                <button className="btn btn-primary" onClick={openCreateCost}>+ Add Cost</button>
              </div>
              <DataTable
                columns={[
                  { key: 'category', label: 'Category', sortable: true },
                  { key: 'description', label: 'Description' },
                  { key: 'amount', label: 'Amount', sortable: true, render: fmtCurrency, align: 'right' },
                  { key: 'currency', label: 'Currency' }
                ]}
                data={estCosts}
                actions={[
                  { label: 'Edit', onClick: openEditCost },
                  { label: 'Delete', onClick: deleteCost }
                ]}
                emptyMessage="No additional costs."
              />
            </div>
          </>
        )}

        {detailTab === 'costsheet' && (
          <>
            {/* View selector — collapsed from a row of pills into a single
                compact dropdown. Sits on one line just above the Cost Sheet
                toolbar. */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
              <label style={{ color: '#94A3B8', fontSize: 12 }}>View:</label>
              <select
                value={costSheetLine}
                onChange={e => setCostSheetLine(e.target.value)}
                style={{
                  padding: '6px 10px', borderRadius: 6,
                  border: '1px solid rgba(255,255,255,0.12)',
                  background: 'rgba(15,23,42,0.6)', color: '#E5E7EB',
                  fontSize: 12.5, outline: 'none', minWidth: 200, cursor: 'pointer',
                }}
              >
                <option value="consolidated">Consolidated (read-only roll-up)</option>
                <option value="estimate">Estimate-Level</option>
                {estLines.map(line => (
                  <option key={line.id} value={line.id}>
                    {line.phase || line.role || 'Line'}
                  </option>
                ))}
              </select>
            </div>
            {costSheetLine === 'consolidated' && (
              <CostSheet estimateId={selectedEst.id} consolidated={true} estimateName={`${selectedEst.name} - Consolidated`} />
            )}
            {costSheetLine === 'estimate' && (
              <CostSheet estimateId={selectedEst.id} estimateName={selectedEst.name} />
            )}
            {costSheetLine !== 'consolidated' && costSheetLine !== 'estimate' && (
              <CostSheet
                key={costSheetLine}
                estimateId={selectedEst.id}
                effortLineId={costSheetLine}
                estimateName={`${selectedEst.name} - ${(estLines.find(l => l.id === costSheetLine) || {}).phase || 'Line'}`}
              />
            )}
          </>
        )}

        {detailTab === 'billingplan' && (
          <div className="card" style={{ padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <h4 style={{ margin: 0 }}>Billing Plan</h4>
                <div style={{ color: '#94A3B8', fontSize: 12, marginTop: 4 }}>
                  Template for the contract billing run. When a contract is created from this estimate,
                  the plan is copied over — header milestones stay on the contract header, line-item
                  milestones re-target to the matching contract line.
                </div>
              </div>
            </div>
            <BillingPlanEditor
              ownerType="estimate"
              ownerId={selectedEst.id}
              totalValue={parseFloat(selectedEst.total_billing) || 0}
              currency={'INR'}
            />
            {selectedEst.pricing_schema_id && (
              <div style={{ marginTop: 18 }}>
                <h4 style={{ margin: 0, marginBottom: 8 }}>Pricing Computation</h4>
                <PricingComputation
                  schemaId={selectedEst.pricing_schema_id}
                  estimateId={selectedEst.id}
                  totalValue={parseFloat(selectedEst.total_billing) || 0}
                  currency={'INR'}
                />
              </div>
            )}
          </div>
        )}

        {/* Line Modal */}
        <Modal title={editingLine ? 'Edit Effort Line' : 'New Effort Line'} isOpen={lineModalOpen} onClose={() => setLineModalOpen(false)} dirty={detailDirty}>
          <div className="form-row">
            <FormField
              label="Item #"
              name="item_number"
              type="number"
              value={lineForm.item_number}
              onChange={(n, v) => { setLineForm(p => ({ ...p, [n]: v })); markDetailDirty(); }}
              min="0"
              step="1"
              placeholder="Auto (next available)"
            />
            <FormField label="Phase" name="phase" value={lineForm.phase} onChange={(n, v) => { setLineForm(p => ({ ...p, [n]: v })); markDetailDirty(); }} />
            <FormField
              label="Role"
              name="role"
              type="select"
              value={lineForm.role}
              onChange={(n, v) => { setLineForm(p => ({ ...p, [n]: v })); markDetailDirty(); }}
              options={ROLE_OPTIONS}
              placeholder="Select role..."
            />
          </div>
          <div className="form-row">
            <FormField label="Effort Days" name="effort_days" type="number" value={lineForm.effort_days} onChange={(n, v) => { setLineForm(p => ({ ...p, [n]: v })); markDetailDirty(); }} min="0" step="0.5" />
            <FormField label="Rate ($/day)" name="rate" type="number" value={lineForm.rate} onChange={(n, v) => { setLineForm(p => ({ ...p, [n]: v })); markDetailDirty(); }} min="0" />
          </div>
          <div className="form-row">
            <FormField label="Extended Cost" name="extended_cost" type="number" value={lineForm.extended_cost} onChange={(n, v) => { setLineForm(p => ({ ...p, [n]: v })); markDetailDirty(); }} min="0" />
            <FormField label="Extended Billing" name="extended_billing" type="number" value={lineForm.extended_billing} onChange={(n, v) => { setLineForm(p => ({ ...p, [n]: v })); markDetailDirty(); }} min="0" />
          </div>
          <FormField label="Notes" name="notes" type="textarea" value={lineForm.notes} onChange={(n, v) => { setLineForm(p => ({ ...p, [n]: v })); markDetailDirty(); }} />
          <div className="form-actions">
            <button className="btn btn-secondary" onClick={() => setLineModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveLine} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </Modal>

        {/* Cost Modal */}
        <Modal title={editingCost ? 'Edit Cost' : 'New Cost'} isOpen={costModalOpen} onClose={() => setCostModalOpen(false)} dirty={detailDirty}>
          <div className="form-row">
            <FormField label="Category" name="category" value={costForm.category} onChange={(n, v) => { setCostForm(p => ({ ...p, [n]: v })); markDetailDirty(); }} />
            <FormField label="Amount" name="amount" type="number" value={costForm.amount} onChange={(n, v) => { setCostForm(p => ({ ...p, [n]: v })); markDetailDirty(); }} min="0" />
          </div>
          <FormField label="Description" name="description" type="textarea" value={costForm.description} onChange={(n, v) => { setCostForm(p => ({ ...p, [n]: v })); markDetailDirty(); }} />
          <div className="form-actions">
            <button className="btn btn-secondary" onClick={() => setCostModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveCost} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </Modal>
      </div>
    );
  }

  // ---- Rate Card detail view ----
  if (selectedRc) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, marginTop: 0 }}>
          <button className="btn btn-secondary" onClick={closeRcDetail}>Back to Rate Cards</button>
          <h3 style={{ margin: 0 }}>{selectedRc.name}</h3>
          <span className={`badge ${STATUS_BADGES[selectedRc.status] || 'badge-gray'}`}>{selectedRc.status}</span>
        </div>

        <div className="card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h4 style={{ margin: 0 }}>Rate Card Lines</h4>
            <button className="btn btn-primary" onClick={openCreateRcLine}>+ Add Role Rate</button>
          </div>
          <DataTable
            columns={[
              { key: 'role', label: 'Role', sortable: true },
              { key: 'billing_rate', label: 'Billing Rate', sortable: true, render: fmtCurrency, align: 'right' },
              { key: 'internal_cost_rate', label: 'Cost Rate', sortable: true, render: fmtCurrency, align: 'right' },
              { key: 'currency', label: 'Currency' },
              { key: '_margin', label: 'Margin %', render: (_, r) => { const b = parseFloat(r.billing_rate) || 0; const c = parseFloat(r.internal_cost_rate) || 0; return b > 0 ? `${Math.round(((b - c) / b) * 100)}%` : '--'; } }
            ]}
            data={rcLines}
            actions={[
              { label: 'Edit', onClick: openEditRcLine },
              { label: 'Delete', onClick: deleteRcLine }
            ]}
            emptyMessage="No rate card lines. Add role rates to this card."
          />
        </div>

        <Modal title={editingRcLine ? 'Edit Role Rate' : 'New Role Rate'} isOpen={rcLineModalOpen} onClose={() => setRcLineModalOpen(false)} dirty={detailDirty}>
          <div className="form-row">
            <FormField
              label="Role"
              name="role"
              type="select"
              value={rcLineForm.role}
              onChange={(n, v) => { setRcLineForm(p => ({ ...p, [n]: v })); markDetailDirty(); }}
              options={ROLE_OPTIONS}
              placeholder="Select role..."
            />
            <FormField label="Currency" name="currency" value={rcLineForm.currency} onChange={(n, v) => { setRcLineForm(p => ({ ...p, [n]: v })); markDetailDirty(); }} />
          </div>
          <div className="form-row">
            <FormField label="Billing Rate" name="billing_rate" type="number" value={rcLineForm.billing_rate} onChange={(n, v) => { setRcLineForm(p => ({ ...p, [n]: v })); markDetailDirty(); }} min="0" />
            <FormField label="Internal Cost Rate" name="internal_cost_rate" type="number" value={rcLineForm.internal_cost_rate} onChange={(n, v) => { setRcLineForm(p => ({ ...p, [n]: v })); markDetailDirty(); }} min="0" />
          </div>
          <div className="form-actions">
            <button className="btn btn-secondary" onClick={() => setRcLineModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveRcLine} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </Modal>
      </div>
    );
  }

  // ---- Main tabbed view ----
  const estColumns = [
    { key: 'name', label: 'Estimate Name', sortable: true },
    { key: 'opportunity_name', label: 'Opportunity', sortable: true, render: (v) => v || '--' },
    { key: 'methodology', label: 'Methodology', sortable: true, render: (v) => v || '--' },
    { key: 'version', label: 'Ver', sortable: true },
    { key: 'total_effort_days', label: 'Days', sortable: true, type: 'number', render: (v) => v || '0' },
    { key: 'total_billing', label: 'Billing', sortable: true, render: fmtCurrency, align: 'right' },
    { key: 'margin_pct', label: 'Margin %', sortable: true, render: (v) => v != null ? `${v}%` : '--' },
    { key: 'status', label: 'Status', sortable: true, render: (v) => <span className={`badge ${STATUS_BADGES[v] || 'badge-gray'}`}>{v}</span> }
  ];

  const rcColumns = [
    { key: 'name', label: 'Card Name', sortable: true },
    { key: 'geography', label: 'Geography', sortable: true, render: (v) => v || '--' },
    { key: 'customer_tier', label: 'Customer Tier', sortable: true, render: (v) => v || '--' },
    { key: 'effective_from', label: 'From', sortable: true, render: (v) => formatDate(v, '--') },
    { key: 'effective_to', label: 'To', sortable: true, render: (v) => formatDate(v, '--') },
    { key: 'status', label: 'Status', sortable: true, render: (v) => <span className={`badge ${STATUS_BADGES[v] || 'badge-gray'}`}>{v}</span> }
  ];

  return (
    <div>
      {/* The tab switcher that used to sit here has been removed — the sidebar
          menu items (Estimates vs Rate Cards) now own the navigation. */}

      {tab === 'estimates' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button className="btn btn-primary" onClick={openCreateEst}>+ New Estimate</button>
          </div>
          <DataTable
            columns={estColumns}
            data={estimates}
            loading={loading}
            actions={[
              { label: 'Line Items', icon: 'lineitems', onClick: openLineItems },
              { label: 'Cost Sheet', icon: 'costsheet', onClick: openCostSheet },
              { label: 'View', onClick: openEstimateDetail },
              { label: 'Edit', onClick: openEditEst },
              { label: 'Duplicate', onClick: duplicateEstimate },
              { label: 'Copy to Opp', onClick: openCopyToOpp },
              { label: 'Delete', onClick: deleteEstimate }
            ]}
            emptyMessage="No estimates found. Create your first effort estimate."
          />
        </div>
      )}

      {tab === 'ratecards' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button className="btn btn-primary" onClick={openCreateRc}>+ New Rate Card</button>
          </div>
          <DataTable
            columns={rcColumns}
            data={rateCards}
            loading={loading}
            actions={[
              { label: 'View Lines', onClick: openRcDetail },
              { label: 'Edit', onClick: openEditRc },
              { label: 'Delete', onClick: deleteRateCard }
            ]}
            emptyMessage="No rate cards found. Create your first rate card."
          />
        </div>
      )}

      {/* Estimate Modal */}
      <Modal title={editingEst ? 'Edit Estimate' : 'New Estimate'} isOpen={estModalOpen} onClose={() => setEstModalOpen(false)} dirty={estDirty}>
        {estErrors._general && <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{estErrors._general}</div>}
        <FormField label="Estimate Name" name="name" value={estForm.name} onChange={(n, v) => { setEstForm(p => ({ ...p, [n]: v })); markEstDirty(); }} error={estErrors.name} required />
        <FormField label="Opportunity" name="opportunity_id" type="select" value={estForm.opportunity_id} onChange={(n, v) => {
          // When the opportunity changes, follow opportunity → customer →
          // customer's default pricing schema and auto-fill the picker if
          // the user hasn't already chosen one. Override is allowed below.
          setEstForm(p => {
            const next = { ...p, [n]: v };
            if (n === 'opportunity_id' && v && !p.pricing_schema_id) {
              const opp = opportunities.find(o => o.id === v);
              if (opp && opp.customer_id) {
                const cust = customers.find(c => c.id === opp.customer_id);
                if (cust && cust.pricing_schema_id) next.pricing_schema_id = cust.pricing_schema_id;
              }
            }
            return next;
          });
          markEstDirty();
        }} options={oppOptions} placeholder="Select opportunity..." required error={estErrors.opportunity_id} />
        <div className="form-row">
          <FormField label="Methodology" name="methodology" value={estForm.methodology} onChange={(n, v) => { setEstForm(p => ({ ...p, [n]: v })); markEstDirty(); }} />
          <FormField label="Status" name="status" type="select" value={estForm.status} onChange={(n, v) => { setEstForm(p => ({ ...p, [n]: v })); markEstDirty(); }} options={STATUS_OPTIONS} />
        </div>
        <FormField label="Pricing Schema" name="pricing_schema_id" type="select"
          value={estForm.pricing_schema_id}
          onChange={(n, v) => { setEstForm(p => ({ ...p, [n]: v })); markEstDirty(); }}
          options={[
            { value: '', label: '— None —' },
            ...pricingSchemas.filter(s => s.status === 'Active').map(s => ({ value: s.id, label: `${s.code} · ${s.name}` })),
          ]}
          placeholder="Select pricing schema..." />
        <div className="form-actions">
          <button className="btn btn-secondary" onClick={() => setEstModalOpen(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={saveEstimate} disabled={saving}>{saving ? 'Saving...' : (editingEst ? 'Update' : 'Create')}</button>
        </div>
      </Modal>

      {/* Copy to Opportunity Modal */}
      <Modal title="Copy Estimate to Another Opportunity" isOpen={copyModalOpen} onClose={() => setCopyModalOpen(false)}>
        <p style={{ color: '#94A3B8', fontSize: 13, marginBottom: 16 }}>
          Copy this estimate (or a specific saved version) to another opportunity. Rates will be refreshed from the active rate card matching the start date.
        </p>
        <FormField label="Target Opportunity" name="copyTargetOpp" type="select" value={copyTargetOpp}
          onChange={(n, v) => setCopyTargetOpp(v)} options={oppOptions} placeholder="Select target opportunity..." required />
        {copyVersions.length > 0 && (
          <FormField label="Version to Copy (leave blank for current)" name="copyVersion" type="select" value={copySelectedVersion}
            onChange={(n, v) => setCopySelectedVersion(v)}
            options={[{ value: '', label: '— Current (latest saved) —' }, ...copyVersions.map(v => ({ value: v.id, label: v.version_label }))]}
          />
        )}
        <FormField label="New Start Date (for rate lookup)" name="copyStartDate" type="date" value={copyStartDate}
          onChange={(n, v) => setCopyStartDate(v)} />
        <div className="form-actions">
          <button className="btn btn-secondary" onClick={() => setCopyModalOpen(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={executeCopy} disabled={!copyTargetOpp || saving}>
            {saving ? 'Copying...' : 'Copy & Refresh Rates'}
          </button>
        </div>
      </Modal>

      {/* Rate Card Modal */}
      <Modal title={editingRc ? 'Edit Rate Card' : 'New Rate Card'} isOpen={rcModalOpen} onClose={() => setRcModalOpen(false)} dirty={rcDirty}>
        {rcErrors._general && <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{rcErrors._general}</div>}
        <FormField label="Card Name" name="name" value={rcForm.name} onChange={(n, v) => { setRcForm(p => ({ ...p, [n]: v })); markRcDirty(); }} error={rcErrors.name} required />
        <div className="form-row">
          <FormField label="Geography" name="geography" value={rcForm.geography} onChange={(n, v) => { setRcForm(p => ({ ...p, [n]: v })); markRcDirty(); }} />
          <FormField label="Customer Tier" name="customer_tier" value={rcForm.customer_tier} onChange={(n, v) => { setRcForm(p => ({ ...p, [n]: v })); markRcDirty(); }} />
        </div>
        <div className="form-row">
          <FormField label="Effective From" name="effective_from" type="date" value={rcForm.effective_from} onChange={(n, v) => { setRcForm(p => ({ ...p, [n]: v })); markRcDirty(); }} />
          <FormField label="Effective To" name="effective_to" type="date" value={rcForm.effective_to} onChange={(n, v) => { setRcForm(p => ({ ...p, [n]: v })); markRcDirty(); }} />
        </div>
        <FormField label="Status" name="status" type="select" value={rcForm.status} onChange={(n, v) => { setRcForm(p => ({ ...p, [n]: v })); markRcDirty(); }} options={['active', 'inactive']} />
        <div className="form-actions">
          <button className="btn btn-secondary" onClick={() => setRcModalOpen(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={saveRateCard} disabled={saving}>{saving ? 'Saving...' : (editingRc ? 'Update' : 'Create')}</button>
        </div>
      </Modal>
    </div>
  );
}
